// ==============================================
// Public tester download — VortexAnchor for Android
// ==============================================
//
// Reached at vortexnav.com/download (the apex redirect preserves the path, so
// it lands here on app.vortexnav.com/download). No account, no auth: a tester
// following a link has neither.
//
// The instructions are written for somebody who has never sideloaded anything.
// Every warning Android will show them is named in the words Android uses, in
// the order they appear, because the usual failure is a tester stopping at
// "This type of file can harm your device" and assuming we sent them malware.
//
// @docs: vortex-api/src/routes/appDownload.ts

import { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import { resolveApiUrl } from '@/lib/api-url';

interface BuildInfo {
  version: string;
  available: boolean;
  sizeBytes: number | null;
  signingCertSha256: string;
}

const APK_URL = `${resolveApiUrl()}/download/vortexanchor.apk`;

function formatMb(bytes: number | null): string {
  if (!bytes) return '';
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

/** Steps are numbered in the text, so the list needs no marker of its own. */
function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-4">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-cyan-500/15 text-base font-semibold text-cyan-300"
      >
        {n}
      </span>
      <div className="min-w-0 flex-1 pb-8">
        <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
        <div className="mt-2 space-y-3 text-base leading-relaxed text-slate-300">
          {children}
        </div>
      </div>
    </li>
  );
}

/** Words Android puts on screen, quoted so they are recognisable at a glance. */
function Screen({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[15px] font-medium text-slate-100">
      {children}
    </span>
  );
}

export function DownloadPage() {
  const [info, setInfo] = useState<BuildInfo | null>(null);
  const [isAndroid, setIsAndroid] = useState(true);

  useEffect(() => {
    setIsAndroid(/android/i.test(navigator.userAgent));
    fetch(`${resolveApiUrl()}/download/vortexanchor.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setInfo(d))
      .catch(() => {
        /* the page is still useful without the size */
      });
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:py-14">
        <header>
          <p className="text-base font-medium uppercase tracking-wide text-cyan-400">
            Vortex Marine · Test build
          </p>
          <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">
            VortexAnchor for Android
          </h1>
          <p className="mt-3 text-lg leading-relaxed text-slate-300">
            An anchor watch that tells you if your boat starts to drag. This is a
            test version, so it is not on the Play Store yet — you install it
            directly. It takes about two minutes.
          </p>
        </header>

        {/* ---------- Download ---------- */}
        <section className="mt-8">
          <a
            href={APK_URL}
            className="flex w-full items-center justify-center gap-3 rounded-xl bg-cyan-500 px-6 py-4 text-lg font-semibold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-400"
          >
            <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
            Download the app
          </a>
          <p className="mt-3 text-center text-base text-slate-400">
            {info?.version ? `Version ${info.version}` : 'Latest test build'}
            {info?.sizeBytes ? ` · ${formatMb(info.sizeBytes)}` : ''} · Android 7.0
            or newer
          </p>
        </section>

        {!isAndroid && (
          <div className="mt-6 flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
            <ComputerDesktopIcon
              className="h-6 w-6 flex-shrink-0 text-amber-400"
              aria-hidden="true"
            />
            <div className="text-base leading-relaxed text-amber-100">
              <strong className="font-semibold">
                You look like you are on a computer.
              </strong>{' '}
              The app only runs on Android. Open{' '}
              <span className="font-mono text-[15px]">vortexnav.com/download</span>{' '}
              on the phone or tablet you want to test with, and press the button
              there.
            </div>
          </div>
        )}

        {/* ---------- Instructions ---------- */}
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-white">
            How to install it, step by step
          </h2>
          <p className="mt-2 text-base leading-relaxed text-slate-400">
            Android will try to talk you out of this two or three times along the
            way. That is normal — it does it for anything not from the Play Store.
            Here is exactly what you will see.
          </p>

          <ol className="mt-8">
            <Step n={1} title="Press the download button above">
              <p>
                Do this <strong>on the Android phone or tablet itself</strong>, not
                on a computer.
              </p>
            </Step>

            <Step n={2} title="Your browser will warn you about the file">
              <p>
                You will see something like{' '}
                <Screen>This type of file can harm your device</Screen>. Choose{' '}
                <Screen>Download anyway</Screen> or <Screen>OK</Screen>.
              </p>
              <p className="text-slate-400">
                It says this about every app file that does not come from the Play
                Store. It is not a virus warning.
              </p>
            </Step>

            <Step n={3} title="Open the file once it has finished">
              <p>
                Tap the download notification. If it has gone, open your{' '}
                <Screen>Files</Screen> app, go to <Screen>Downloads</Screen>, and
                tap <span className="font-mono text-[15px]">VortexAnchor</span>.
              </p>
            </Step>

            <Step n={4} title="Give permission, once">
              <p>
                Android will say{' '}
                <Screen>
                  For your security, your phone is not allowed to install unknown
                  apps from this source
                </Screen>
                .
              </p>
              <p>
                Tap <Screen>Settings</Screen>, turn on{' '}
                <Screen>Allow from this source</Screen>, then press the{' '}
                <strong>back</strong> arrow. The install carries on where it left
                off.
              </p>
              <p className="text-slate-400">
                You are allowing your browser to install this one app. You can turn
                it off again afterwards if you prefer.
              </p>
            </Step>

            <Step n={5} title="If Play Protect appears, choose to continue">
              <p>
                Some devices show <Screen>Unsafe app blocked</Screen> or offer to
                scan the app. Tap <Screen>More details</Screen>, then{' '}
                <Screen>Install anyway</Screen>.
              </p>
              <p className="text-slate-400">
                Google flags anything it has not seen before. Because this build is
                not on the Play Store, it has not seen it.
              </p>
            </Step>

            <Step n={6} title="Tap Install, then Open">
              <p>
                That is it. The app asks for location permission the first time you
                set an anchor — it needs that to know if the boat has moved. Choose{' '}
                <Screen>While using the app</Screen> or{' '}
                <Screen>Allow all the time</Screen>.
              </p>
            </Step>
          </ol>
        </section>

        {/* ---------- Troubleshooting ---------- */}
        <section className="mt-4 rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <h2 className="flex items-center gap-2 text-xl font-bold text-white">
            <ExclamationTriangleIcon
              className="h-5 w-5 text-amber-400"
              aria-hidden="true"
            />
            If something goes wrong
          </h2>
          <dl className="mt-4 space-y-4 text-base leading-relaxed">
            <div>
              <dt className="font-semibold text-slate-100">
                It says “App not installed”
              </dt>
              <dd className="mt-1 text-slate-300">
                Usually an older copy is already on the device. Uninstall
                VortexAnchor, then tap the downloaded file again.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-100">
                I cannot find the downloaded file
              </dt>
              <dd className="mt-1 text-slate-300">
                Open the <Screen>Files</Screen> app and look in{' '}
                <Screen>Downloads</Screen>. On Samsung devices this app is called{' '}
                <Screen>My Files</Screen>.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-100">
                There is no “Allow from this source” switch
              </dt>
              <dd className="mt-1 text-slate-300">
                On older Android, go to <Screen>Settings</Screen> →{' '}
                <Screen>Security</Screen> and turn on{' '}
                <Screen>Unknown sources</Screen>, then tap the file again.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-100">
                The download will not start
              </dt>
              <dd className="mt-1 text-slate-300">
                Check you are on WiFi and have about{' '}
                {info?.sizeBytes ? formatMb(info.sizeBytes) : '60 MB'} free, then
                try again.
              </dd>
            </div>
          </dl>
        </section>

        {/* ---------- Trust ---------- */}
        <section className="mt-6 flex gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <ShieldCheckIcon
            className="h-6 w-6 flex-shrink-0 text-emerald-400"
            aria-hidden="true"
          />
          <div className="text-base leading-relaxed text-slate-300">
            <p>
              <strong className="font-semibold text-slate-100">
                This build is signed by Vortex Marine.
              </strong>{' '}
              Later updates use the same signature, so you can install them over
              the top without losing your boat settings or anchor history.
            </p>
            {info?.signingCertSha256 && (
              <p className="mt-2 break-all font-mono text-[13px] text-slate-500">
                {info.signingCertSha256}
              </p>
            )}
          </div>
        </section>

        <footer className="mt-10 flex items-start gap-3 border-t border-slate-800 pt-6 text-base leading-relaxed text-slate-400">
          <DevicePhoneMobileIcon
            className="h-5 w-5 flex-shrink-0"
            aria-hidden="true"
          />
          <p>
            Found a problem, or something confusing? Tell us what you were doing
            and what happened — that is exactly what we need from a test build.
          </p>
        </footer>
      </div>
    </div>
  );
}
